/** Groundwork Tasks v1 — shared client types + helpers. */
import type { WorkCounts } from "./counts";
import type { MadeBy } from "./task-made-by";

export type TaskPriority = "normal" | "urgent";
/** `handed_back` = finished by the assignee, waiting on the asker's confirm (#1645). */
export type TaskStatus = "open" | "handed_back" | "done" | "canceled";

/** Where a task sits on the Sent → Confirmed line (server: services/taskLoop.ts). */
export type TaskStage = "sent" | "seen" | "working" | "handed_back" | "cant_do" | "confirmed" | "canceled";

export interface Task {
    id: string;
    title: string;
    /** V2 plain-language rewrite; the original `title` is always preserved. */
    displayTitle: string | null;
    source: string;
    intent: string | null;
    snoozeCount: number;
    waitingOn: string | null;
    waitingUntil: string | null;
    detailsRich: string | null;
    typeId: string | null;
    typeName: string | null;
    quickAction: string | null;
    promptResolutionNote: boolean;
    priority: TaskPriority;
    status: TaskStatus;
    snoozedUntil: string | null;
    assigneeUserId: string | null;
    assigneeName: string | null;
    assigneeQueueId: string | null;
    queueName: string | null;
    reporterUserId: string | null;
    reporterName: string | null;
    dueDate: string | null;
    resolutionNote: string | null;
    cancelReason: string | null;
    createdAt: string;
    updatedAt: string;
    doneAt: string | null;
    // ── Tasks talk back (#1645) ──
    stage: TaskStage;
    seenAt: string | null;
    startedAt: string | null;
    handedBackAt: string | null;
    handedBackBy: string | null;
    handedBackByName: string | null;
    confirmedAt: string | null;
    confirmedByName: string | null;
    bumped: boolean;
    autoBumped: boolean;
    /** #1842: why it is pinned first. Older servers omit it. */
    bumpReason?: BumpReason | null;
    bumpedAt: string | null;
    bumpedByName: string | null;
    passedBackAt: string | null;
    passedBackByName: string | null;
    passReason: string | null;
    doneLooksLike: string | null;
    /** #1842: the doer's tick at hand-back; null = nothing to tick. */
    doneLooksLikeMet?: boolean | null;
    autoBump: boolean;
    /** A "What's new" note's copy: read it, tap Got it — no Done. */
    whatsNewNoteId?: string | null;
    /** Shared tasks (10/16): one Done finishes it, or each person checks off a part. */
    doneRule?: DoneRule;
    /** More than one person is on it. */
    shared?: boolean;
    /** Tasks Both Ways slice 3: how a hand-back ended — done, or can't do. */
    outcome?: "done" | "cant_do" | null;
    cantDoReason?: string | null;
    cantDoReasonKey?: string | null;
    /** Slice 5: who or what made it — null when a person asked. */
    madeBy?: MadeBy | null;
}

export type DoneRule = "anyone" | "everyone";

/** One person on a shared task, and where they stand. */
export type HolderState = "done" | "working" | "waiting" | "opened" | "not_opened";

export interface TaskHolder {
    userId: string;
    name: string;
    isLead: boolean;
    state: HolderState;
    partDoneAt: string | null;
    partNote: string | null;
}

export const HOLDER_STATE_WORDS: Record<HolderState, string> = {
    done: "Done",
    working: "Working on it",
    waiting: "Waiting on someone",
    opened: "Opened, not started",
    not_opened: "Not opened",
};

/** Why a task is pinned first (#1842): someone asked for an update, it sat unopened, or it is due. */
export type BumpReason = "asked" | "unopened" | "due";

/**
 * The words on a pinned task (#1842). One place, so the Start here stamp and
 * the drawer banner never disagree. `who` is the asker's (or bumper's) name.
 */
export function bumpWords(
    t: { bumpReason?: BumpReason | null; autoBumped: boolean; dueDate: string | null; createdAt: string },
    who: string,
    today: string = new Date().toLocaleDateString("en-CA"),
): { head: string; tail: string; tone: "ask" | "due" } {
    const reason: BumpReason = t.bumpReason ?? (t.autoBumped ? "unopened" : "asked");
    if (reason === "due") {
        const late = t.dueDate != null && t.dueDate < today;
        return {
            head: late ? `Late · ${who} is waiting on this.` : `Due today · ${who} is waiting on this.`,
            tail: "",
            tone: "due",
        };
    }
    if (reason === "unopened") {
        return { head: `${who} is asking about this.`, tail: "It hasn't been opened in 2 working hours.", tone: "ask" };
    }
    return { head: `${who} wants an update.`, tail: `Waiting ${waitedFor(t.createdAt)}.`, tone: "ask" };
}

/**
 * The ONE record chip on a task row (Tasks Both Ways slice 2, 10/28): the
 * task's job first, then device, customer, location, then paperwork. One tap
 * opens it. Null when the task links nothing.
 */
export interface TaskRecord {
    entityType: string;
    entityId: string;
    label: string;
    href: string;
}

export interface TaskLink {
    entityType: string;
    entityId: string;
    label: string;
    href: string;
}

export interface TaskComment {
    id: string;
    authorUserId: string;
    authorName: string | null;
    body: string;
    createdAt: string;
}

export interface TaskAttachment {
    id: string;
    fileName: string;
    fileSize: number;
    mimeType: string | null;
    uploadedBy: string;
    createdAt: string;
}

export interface TaskDetailResponse {
    task: Task;
    links: TaskLink[];
    /** Everyone on a shared task (empty for a one-person task). */
    holders?: TaskHolder[];
    comments: TaskComment[];
    attachments: TaskAttachment[];
}

/**
 * Inbox feed rows are human tasks only — system-generated work (review
 * flags, intake questions) stays in its own queues, never in the inbox.
 */
export interface InboxItem {
    /** The one record chip (slice 2). */
    record?: TaskRecord | null;
    kind: "task";
    id: string;
    title: string;
    priority: TaskPriority;
    createdAt: string;
    href: string;
    claimable: boolean;
    queueName: string | null;
    typeName: string | null;
    snoozedUntil: string | null;
    dueDate: string | null;
}

export interface InboxResponse {
    items: InboxItem[];
    queues: Array<{ id: string; name: string; unclaimedCount: number }>;
    /** Needs you. Same server number as `counts.needsYou`; kept for old callers. */
    count: number;
    counts: WorkCounts;
}

export interface TaskType {
    id: string;
    name: string;
    defaultQueueId: string | null;
    defaultQueueName: string | null;
    quickAction: string | null;
    promptResolutionNote: boolean;
    sort: number;
    isActive: boolean;
}

export interface TaskQueueSummary {
    id: string;
    name: string;
    isActive: boolean;
    isOfficeQueue: boolean;
    openTaskCount: number;
    /** Who an unclaimed item lands on after one working day (2026-09-29). */
    ownerUserId: string | null;
    ownerName: string | null;
    members: Array<{ userId: string; name: string; implicit: boolean }>;
}

export type TaskLinkInput = { entityType: string; entityId: string };

/** Age in whole days since the item entered the world. */
export function ageDays(createdAt: string): number {
    return Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000);
}

/**
 * Stale visibility without due dates: chip appears at 3 days, amber at 7
 * (spec §Work Inbox).
 */
export function ageChip(createdAt: string): { label: string; amber: boolean } | null {
    const days = ageDays(createdAt);
    if (days < 3) return null;
    return { label: `${days}d`, amber: days >= 7 };
}

/**
 * Quick-action deep links: map the type's quick_action + linked entities to
 * the screen that resolves the task.
 */
export function quickActionTarget(
    quickAction: string | null,
    links: TaskLink[],
): { label: string; href: string } | null {
    if (!quickAction) return null;
    const byType = (t: string) => links.find((l) => l.entityType === t);
    const job = byType("job");
    switch (quickAction) {
        case "create_estimate":
            return job
                ? { label: "Create estimate", href: `/commercial/new?type=estimate&jobId=${job.entityId}` }
                : { label: "Create estimate", href: "/commercial/new?type=estimate" };
        case "open_grid":
            return { label: "Open Grid", href: job ? `/dispatch?jobId=${job.entityId}` : "/dispatch" };
        case "open_invoice": {
            const invoice = byType("invoice");
            return invoice
                ? { label: "Open invoice", href: invoice.href }
                : { label: "Open invoices", href: "/commercial?type=invoice" };
        }
        case "city_filing":
            return { label: "City Filing Queue", href: "/submissions" };
        case "purchase_orders":
            return { label: "Purchase orders", href: "/purchasing" };
        case "pricebook":
            return { label: "Pricebook", href: "/pricebook" };
        case "job_photos":
            return job ? { label: "Job photos", href: `/jobs/${job.entityId}?tab=photos` } : null;
        default:
            return null;
    }
}

// ── Work Inbox V2 — triage feed ─────────────────────────────────────────────

export type TriageLane = "quick" | "real" | "waiting";
export type TriageHeat = "none" | "amber" | "red";
export type EffortClass = "quick" | "real" | "big";

export interface TriagedTask {
    /** The one record chip (slice 2). */
    record?: TaskRecord | null;
    /** Slice 5: who or what made it — null when a person asked. */
    madeBy?: MadeBy | null;
    id: string;
    /** Plain-language display title (falls back to the original server-side). */
    title: string;
    originalTitle: string;
    lane: TriageLane;
    heat: TriageHeat;
    heatReason: string | null;
    effortMinutes: number;
    effortClass: EffortClass;
    needsEffortAnswer: boolean;
    whyFirst: string;
    priority: "normal" | "high" | "urgent";
    source: string;
    typeName: string | null;
    createdAt: string;
    dueDate: string | null;
    waitingOn: string | null;
    waitingUntil: string | null;
    snoozeCount: number;
    href: string;
    claimable: boolean;
    queueName: string | null;
    assigneeName: string | null;
    // ── Tasks talk back (#1645) ──
    /** Who asked, when it's someone other than me. */
    askedByName: string | null;
    askedByUserId: string | null;
    /** "X is asking about this" — pins it first. */
    bumped: boolean;
    autoBumped: boolean;
    bumpReason?: BumpReason | null;
    bumpedByName: string | null;
    seen: boolean;
    started: boolean;
    /** Came to me because nobody in this queue took it in a working day. */
    escalatedFromQueue: string | null;
    /** Shared tasks (10/16): null when one person holds it. */
    shared?: { people: number; partsDone: number; doneRule: DoneRule; helping: boolean } | null;
}

/** Something I asked for that came back to me (#1645). */
export interface BackItem {
    /** The one record chip (slice 2). */
    record?: TaskRecord | null;
    id: string;
    /** "cant_do" (slice 3): back in red with the reason in `note`. */
    kind: "handed_back" | "passed_back" | "cant_do";
    title: string;
    byName: string | null;
    byUserId: string | null;
    at: string | null;
    note: string | null;
    queueName: string | null;
    attachmentCount: number;
    doneLooksLike: string | null;
    /** #1842: did they tick "done looks like"? null = nothing to tick. */
    doneLooksLikeMet?: boolean | null;
    href: string;
}

export type SentFlag = "needs_you" | "late" | "stuck" | "waiting";

export interface SentItem {
    /** The one record chip (slice 2). */
    record?: TaskRecord | null;
    id: string;
    title: string;
    status: TaskStatus;
    stage: TaskStage;
    stageIndex: number;
    flags: SentFlag[];
    late: boolean;
    bumped: boolean;
    autoBumped: boolean;
    bumpReason?: BumpReason | null;
    passedBack: boolean;
    assigneeUserId: string | null;
    assigneeName: string | null;
    queueName: string | null;
    dueDate: string | null;
    priority: string;
    waitingOn: string | null;
    lastEvent: string;
    note: string | null;
    passReason: string | null;
    createdAt: string | null;
    seenAt: string | null;
    startedAt: string | null;
    handedBackAt: string | null;
    doneAt: string | null;
    /** Two-way part 2 (#1842): they did something since you last looked. */
    hasNews?: boolean;
    newsKind?: "handed_back" | "passed_back" | "reply" | null;
    newsAt?: string | null;
    doneLooksLike?: string | null;
    doneLooksLikeMet?: boolean | null;
    href: string;
    /** Slice 3: they can't do it — the red stage — and why. */
    cantDo?: boolean;
    cantDoReason?: string | null;
    /** Slice 4: the quiet done mark — handed back done, or closed. */
    doneQuiet?: boolean;
    autoClosedAt?: string | null;
    /** Working hours until a done hand-back closes by itself (null otherwise). */
    closesInWorkingHours?: number | null;
}

export interface SentResponse {
    items: SentItem[];
    counts: {
        all: number;
        out: number;
        needsYou: number;
        late: number;
        stuck: number;
        waiting: number;
        /** #1842: confirmed in the last 7 days, and rows with something new. */
        done?: number;
        news?: number;
        /** Slice 4: green checks this week (closed or not), and red can't-dos. */
        doneWeek?: number;
        cantDo?: number;
    };
}

export interface TimelineEntry {
    at: string;
    kind: string;
    text: string;
    tone: "neutral" | "good" | "warn" | "bad" | "wait";
}

export interface TeamResponse {
    people: Array<{
        userId: string;
        name: string;
        open: number;
        late: number;
        notOpened: number;
        waiting: number;
        doneThisWeek: number;
        owesConfirm: number;
        oldestUntouched: { id: string; title: string; createdAt: string; seen: boolean } | null;
    }>;
    queues: Array<{
        queueId: string;
        name: string;
        ownerName: string | null;
        unclaimed: number;
        oldest: { id: string; title: string; createdAt: string; passedBackBy: string | null } | null;
    }>;
    week: { closed: number; closedWithNote: number; sentBack: number };
    /** 10/16: every office person's open tasks, never-opened first. */
    holding?: HoldingPerson[];
    /** The score table and week numbers are for the owner and managers. */
    canSeeScores?: boolean;
    /** Who is looking (so their own row says "This is yours"). */
    me?: string;
}

export type HoldingStage = "not_opened" | "opened" | "working" | "waiting" | "snoozed";

export interface HoldingTask {
    /** The one record chip (slice 2). */
    record?: TaskRecord | null;
    id: string;
    title: string;
    createdAt: string;
    stage: HoldingStage;
    late: boolean;
    dueDate: string | null;
    askedByName: string | null;
    ownTask: boolean;
    doneRule: DoneRule;
    alsoOn: Array<{ userId: string; name: string; partDone: boolean }>;
}

export interface HoldingPerson {
    userId: string;
    name: string;
    open: number;
    late: number;
    notOpened: number;
    tasks: HoldingTask[];
}

/** Slice 3 (10/28): the same six stage words as everywhere else (lib/task-stages.ts). */
export const HOLDING_STAGE_WORDS: Record<HoldingStage, string> = {
    not_opened: "New",
    opened: "Seen",
    working: "Doing",
    waiting: "Waiting on…",
    snoozed: "Put off for now",
};

/** "Regina Wagoner" → "Regina". */
export function firstName(name: string | null | undefined): string {
    return (name ?? "").trim().split(/\s+/)[0] || "Someone";
}

/** "3 days", "7 hours", "12 min" since an instant — how long someone has waited. */
export function waitedFor(iso: string | null | undefined, now = new Date()): string {
    if (!iso) return "";
    const mins = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
    if (mins < 60) return `${mins} min`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? "" : "s"}`;
}

/** True when an API error is the server asking for a hand-back note. */
export function isNoteRequired(e: unknown): boolean {
    const body = (e as { body?: unknown } | null)?.body as { error?: string } | null | undefined;
    return Boolean(body && typeof body === "object" && body.error === "NOTE_REQUIRED");
}

/** What a NOTE_REQUIRED answer carries for the hand-back dialog (#1842). */
export function noteRequiredDetails(e: unknown): { askerName: string | null; doneLooksLike: string | null } {
    const body = (e as { body?: unknown } | null)?.body as
        | { askerName?: unknown; doneLooksLike?: unknown }
        | null
        | undefined;
    const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);
    return { askerName: str(body?.askerName), doneLooksLike: str(body?.doneLooksLike) };
}

export interface TriageResponse {
    spotlight: TriagedTask | null;
    lanes: { quick: TriagedTask[]; real: TriagedTask[]; waiting: TriagedTask[] };
    snoozed: Array<{ id: string; title: string; snoozedUntil: string; snoozeCount: number; href: string }>;
    quickMinutesTotal: number;
    /** What I asked for that came back to me (#1645). */
    backToYou: BackItem[];
    /**
     * The four named counts, computed server-side from the same rows these
     * lanes were built from. The page heading reads this — it must never count
     * the rows it just rendered (#1203 / D2).
     */
    counts: WorkCounts;
}

/** "After lunch" chip: 1pm today (tomorrow 7am if lunch already passed). */
export function afterLunchIso(now = new Date()): string {
    const d = new Date(now);
    d.setHours(13, 0, 0, 0);
    if (d.getTime() <= now.getTime()) return tomorrowMorningIso(now);
    return d.toISOString();
}

/** "Tomorrow 7am" chip. */
export function tomorrowMorningIso(now = new Date()): string {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    d.setHours(7, 0, 0, 0);
    return d.toISOString();
}

/**
 * Title coaching (mirrors server VERB_STARTERS): a good task title starts
 * with an imperative verb — "Fix device list…", not "Device list issue".
 */
const TITLE_VERBS = new Set([
    "add",
    "answer",
    "approve",
    "assign",
    "book",
    "build",
    "buy",
    "call",
    "cancel",
    "chase",
    "check",
    "clean",
    "clear",
    "close",
    "collect",
    "confirm",
    "correct",
    "create",
    "email",
    "enter",
    "file",
    "finish",
    "fix",
    "follow",
    "get",
    "invoice",
    "log",
    "look",
    "mail",
    "make",
    "map",
    "match",
    "move",
    "order",
    "pay",
    "print",
    "quote",
    "read",
    "rebook",
    "recheck",
    "reconcile",
    "record",
    "refund",
    "remove",
    "reply",
    "reprice",
    "reschedule",
    "resend",
    "resolve",
    "return",
    "review",
    "revise",
    "schedule",
    "send",
    "set",
    "ship",
    "sign",
    "submit",
    "swap",
    "text",
    "update",
    "upload",
    "verify",
    "write",
]);

export function titleHasVerb(title: string): boolean {
    const first = title
        .trim()
        .split(/\s+/)[0]
        ?.toLowerCase()
        .replace(/[^a-z]/g, "");
    return first ? TITLE_VERBS.has(first) : false;
}

/** Suggest a verb-first rewrite hint for common noun-first patterns. */
export function suggestVerbTitle(title: string): string | null {
    const t = title.trim();
    if (!t || titleHasVerb(t)) return null;
    const lower = t.toLowerCase();
    if (/(wrong|incorrect|bad|broken|missing)/.test(lower)) return `Fix ${t.charAt(0).toLowerCase()}${t.slice(1)}`;
    if (/question/.test(lower)) return `Answer ${t.charAt(0).toLowerCase()}${t.slice(1)}`;
    if (/quote|estimate/.test(lower)) return `Build ${t.charAt(0).toLowerCase()}${t.slice(1)}`;
    return `Start with a verb — e.g. "Fix …", "Call …", "Send …"`;
}

/** Render `@[Name](user:id)` mention tokens as plain highlighted names. */
export function renderMentionText(body: string): string {
    return body.replace(/@\[([^\]\n]{1,120})\]\(user:[0-9a-fA-F-]{36}\)/g, "@$1");
}

export const UNDO_TOAST_MS = 10_000;
